import { participatesInSettlement, personOrder, resolveTransaction } from './resolve.ts';
import { simplifyDebts } from './simplify.ts';
import type {
  CurrencyLedger,
  IsoDate,
  Issue,
  PersonId,
  PersonLedger,
  ResolvedItem,
  SettlementContext,
  SettlementRecord,
  SettlementResult,
  TransactionInput,
} from './types.ts';

export type SettlementQuery = {
  /** Include transactions booked on or after this date. */
  readonly from?: IsoDate | null;
  /** Include transactions and settlements up to and including this date. */
  readonly to?: IsoDate | null;
};

type Accumulator = { paid: bigint; owed: bigint; settled: bigint };

/**
 * Computes who paid what, who owes what and the suggested transfers, per currency.
 * Pure and deterministic: the server and both apps get identical numbers from the same data.
 */
export function computeSettlement(
  transactions: readonly TransactionInput[],
  settlements: readonly SettlementRecord[],
  context: SettlementContext,
  query: SettlementQuery = {},
): SettlementResult {
  const order = personOrder(context);
  const items: ResolvedItem[] = [];
  const issues: Issue[] = [];
  const inRange = (date: IsoDate) =>
    (query.from === undefined || query.from === null || date >= query.from) &&
    (query.to === undefined || query.to === null || date <= query.to);

  // Stable processing order keeps the result independent of input order.
  const sorted = [...transactions].sort((a, b) =>
    a.bookingDate !== b.bookingDate ? (a.bookingDate < b.bookingDate ? -1 : 1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );
  for (const transaction of sorted) {
    if (!participatesInSettlement(transaction) || !inRange(transaction.bookingDate)) continue;
    const resolved = resolveTransaction(transaction, context, order);
    items.push(...resolved.items);
    issues.push(...resolved.issues);
  }

  const ledgers = new Map<string, Map<PersonId, Accumulator>>();
  const accountFor = (currency: string, personId: PersonId): Accumulator => {
    let ledger = ledgers.get(currency);
    if (!ledger) {
      ledger = new Map();
      ledgers.set(currency, ledger);
    }
    let entry = ledger.get(personId);
    if (!entry) {
      entry = { paid: 0n, owed: 0n, settled: 0n };
      ledger.set(personId, entry);
    }
    return entry;
  };

  for (const item of items) {
    for (const p of item.paid) accountFor(item.currency, p.personId).paid += p.amountMinor;
    for (const o of item.owed) accountFor(item.currency, o.personId).owed += o.amountMinor;
  }

  for (const settlement of settlements) {
    if (settlement.status !== 'SETTLED') continue;
    if (query.to !== undefined && query.to !== null && settlement.settledOn > query.to) continue;
    if (query.from !== undefined && query.from !== null && settlement.settledOn < query.from) continue;
    for (const transfer of settlement.transfers) {
      accountFor(settlement.currency, transfer.fromPersonId).settled += transfer.amountMinor;
      accountFor(settlement.currency, transfer.toPersonId).settled -= transfer.amountMinor;
    }
  }

  const rank = (id: PersonId) => order.get(id) ?? Number.MAX_SAFE_INTEGER;
  const currencyLedgers: CurrencyLedger[] = [...ledgers.entries()]
    .sort(([a], [b]) => (a === context.baseCurrency ? -1 : b === context.baseCurrency ? 1 : a < b ? -1 : 1))
    .map(([currency, entries]) => {
      const persons: PersonLedger[] = [...entries.entries()]
        .sort(([a], [b]) => rank(a) - rank(b))
        .map(([personId, acc]) => ({
          personId,
          paidMinor: acc.paid,
          owedMinor: acc.owed,
          settledMinor: acc.settled,
          netMinor: acc.paid - acc.owed + acc.settled,
        }));
      return {
        currency,
        persons,
        suggestedTransfers: simplifyDebts(persons, order),
      };
    });

  return { ledgers: currencyLedgers, items, issues };
}

